import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { EditableText } from '../components/Editable';
import { ProductTile, productStatus } from '../shell/Sidebar';
import { useProducts } from '../product/ProductContext';
import { PRODUCT_COLORS, isOnboarding } from '../product/productUtils';
import { useBilling } from '../billing/BillingContext';

const ManageProductsScreen = () => {
    const navigate = useNavigate();
    const { products, currentProduct, icps, selectProduct, updateProduct, deleteProduct } = useProducts();
    const { usage, openUpgrade } = useBilling();
    const [error, setError] = useState('');

    const addProduct = () => {
        if (usage && products.length >= usage.products.limit) {
            openUpgrade({ metric: 'products', limit: usage.products.limit, used: products.length });
            return;
        }
        navigate('/products/new');
    };

    const run = async (action, message) => {
        setError('');
        try {
            await action();
        } catch (e) {
            setError(message);
        }
    };

    const handleDelete = (product) => {
        const typed = window.prompt(
            `Delete ${product.name}? Its profile, ICPs, plans, memory and tracked links are deleted too, and its links stop redirecting. Type the product name to confirm.`
        );
        if (typed == null) return;
        if (typed.trim() !== product.name.trim()) {
            setError("The name didn't match, so nothing was deleted.");
            return;
        }
        run(() => deleteProduct(product.id), `Couldn't delete ${product.name}.`);
    };

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Settings</div>
                    <h1 className="ls-page-title">Products</h1>
                </div>
                <button type="button" className="ls-btn ls-btn-primary" onClick={addProduct}>
                    <Icon name="add" size={18} />Add product
                </button>
            </div>

            {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}

            <div className="ls-card" style={{ padding: 8, gap: 0 }}>
                {products.map((product, index) => {
                    const isCurrent = product.id === currentProduct?.id;
                    const status = productStatus(product, isCurrent ? icps.length : product.icpCount);
                    return (
                        <div
                            key={product.id}
                            style={{
                                display: 'flex', gap: 14, alignItems: 'center', padding: '14px 12px', flexWrap: 'wrap',
                                borderTop: index ? '1px solid var(--subtle)' : 0,
                            }}
                        >
                            <ProductTile product={product} size={40} radius={10} fontSize={17} />
                            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                                <div style={{ fontSize: 15, fontWeight: 600 }}>
                                    <EditableText
                                        value={product.name}
                                        label={`${product.name} name`}
                                        onSave={(name) => name && run(() => updateProduct(product.id, { name }), "Couldn't rename the product.")}
                                    />
                                </div>
                                <div style={{ fontSize: 12, color: status.resume ? '#b54708' : 'var(--muted)' }}>
                                    {status.text}{isCurrent ? ' · Current' : ''}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }} role="radiogroup" aria-label={`${product.name} color`}>
                                {PRODUCT_COLORS.map(color => (
                                    <button
                                        type="button"
                                        key={color}
                                        role="radio"
                                        aria-checked={product.color === color}
                                        aria-label={color}
                                        onClick={() => run(() => updateProduct(product.id, { color }), "Couldn't change the color.")}
                                        style={{
                                            width: 20, height: 20, borderRadius: 6, background: color, cursor: 'pointer',
                                            border: product.color === color ? '2px solid var(--ink)' : '2px solid #fff',
                                            boxShadow: '0 0 0 1px var(--border)',
                                        }}
                                    />
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {isOnboarding(product) ? (
                                    <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate(`/products/${product.id}/setup`)}>
                                        Resume setup
                                    </button>
                                ) : !isCurrent && (
                                    <button
                                        type="button"
                                        className="ls-btn ls-btn-secondary"
                                        onClick={() => {
                                            selectProduct(product.id);
                                            navigate('/dashboard');
                                        }}
                                    >
                                        Switch to
                                    </button>
                                )}
                                <button type="button" className="ls-btn ls-btn-ghost" aria-label={`Delete ${product.name}`} title="Delete product" onClick={() => handleDelete(product)}>
                                    <Icon name="delete" size={20} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default ManageProductsScreen;
